import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api";
import { Typography, Spin, Tag, Button, Empty, message, Modal, Input, App } from "antd";

import "./AdminUserPosts.css";

const { Title, Text } = Typography;

const TABS = [
  { key: "commercial", label: "Commercial Spaces", api: "commercial-spaces", dataKey: "commercial" },
  { key: "stalls", label: "Stalls", api: "stalls", dataKey: "stalls" },
  { key: "franchises", label: "Franchises", api: "franchises", dataKey: "franchises" },
  { key: "businesses", label: "Businesses", api: "businesses", dataKey: "businesses" },
  { key: "ads", label: "Ads", api: "ads", dataKey: "ads" },
];

const getAdsArray = (data) =>
  Array.isArray(data?.ads)
    ? data.ads
    : Array.isArray(data?.ads?.ads)
      ? data.ads.ads
      : [];

/* ── Inner component (needs App context for message) ─────── */
function AdminUserPostsInner() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("commercial");

  /* ── Reject modal state ─────────────────────────────────── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const { message: msg } = App.useApp();

  /* ── Fetch ──────────────────────────────────────────────── */
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get(`/users/user-posts/${id}`);
      setData(res.data);
    } catch {
      msg.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  /* ── Modal helpers ──────────────────────────────────────── */
  const openRejectModal = (type, postId) => {
    setSelectedType(type);
    setSelectedId(postId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setReason("");
    setSelectedId(null);
    setSelectedType(null);
  };

  /* ── Actions ────────────────────────────────────────────── */
  const handleApprove = async (type, postId) => {
    try {
      await API.put(`/${type}/approve/${postId}`);
      msg.success("Approved");
      fetchPosts();
    } catch {
      msg.error("Approval failed");
    }
  };

  const handleConfirmReject = async () => {
    try {
      const res = await API.put(`/${selectedType}/reject/${selectedId}`, { reason });
      msg.success(res.data?.message || "Listing rejected with reason");
      closeModal();
      fetchPosts();
    } catch (error) {
      msg.error(error?.response?.data?.message || "Reject failed");
    }
  };

  const handleActivate = async (type, postId, item) => {
    if (item.approvalStatus !== "approved") { msg.warning("Approve first"); return; }
    try {
      await API.put(`/${type}/admin/${postId}/activate`);
      msg.success("Activated");
      fetchPosts();
    } catch {
      msg.error("Activation failed");
    }
  };

  const handleDeactivate = async (type, postId) => {
    try {
      await API.put(`/${type}/admin/${postId}/deactivate`);
      msg.success("Deactivated");
      fetchPosts();
    } catch {
      msg.error("Deactivation failed");
    }
  };

  const handleView = (type, slug) => {
    const routes = {
      "commercial-spaces": `/commercial-space/${slug}`,
      stalls: `/stall/${slug}`,
      franchises: `/franchise/${slug}`,
      businesses: `/business/${slug}`,
      ads: `/ads/${slug}`,
    };
    navigate(routes[type]);
  };

  /* ── Card ───────────────────────────────────────────────── */
  const renderCard = (item, type) => (
    <div className="aup-card" key={item._id}>
      <div className="aup-card__img-wrap">
        <img
          alt="listing"
          src={item.images?.[0] || "https://placehold.co/400x250?text=No+Image"}
          className="aup-card__img"
        />
      </div>

      <div className="aup-card__body">
        <Title
          level={5}
          className="aup-card__title"
          title={item.title || item.companyName || item.brandName}
        >
          {item.title || item.companyName || item.brandName}
        </Title>

        <Text type="secondary" className="aup-card__location">
          {
            type === "franchises"
              ? `${item.headquarters?.city || "N/A"}, ${item.headquarters?.state || ""}`
              : `${item.location?.city || "N/A"}, ${item.location?.state || ""}`
          }
        </Text>

        <Text strong className="aup-card__price">
          {
            type === "franchises"
              ? `₹${item.franchiseDetails?.investmentRequired?.toLocaleString("en-IN") || "N/A"}`
              : type === "businesses"
                ? item.industry || "N/A"
                : `₹${item.pricing?.price?.toLocaleString("en-IN") || "N/A"}`
          }
        </Text>

        <div className="aup-card__tags">
          <Tag className={`aup-tag aup-tag--status aup-tag--${item.status}`}>
            {item.status}
          </Tag>
          <Tag className={`aup-tag aup-tag--approval aup-tag--${item.approvalStatus}`}>
            {item.approvalStatus}
          </Tag>
        </div>

        <div className="aup-card__actions">
          <Button onClick={() => handleView(type, item.slug)} size="small">
            View
          </Button>

          {item.approvalStatus === "pending" && (
            <>
              <Button onClick={() => handleApprove(type, item._id)}>Approve</Button>
              <Button danger onClick={() => openRejectModal(type, item._id)}>Reject</Button>
            </>
          )}

          {item.status === "active" ? (
            <Button danger onClick={() => handleDeactivate(type, item._id)} size="small">
              Deactivate
            </Button>
          ) : item.approvalStatus === "approved" ? (
            <Button onClick={() => handleActivate(type, item._id, item)} size="small">
              Activate
            </Button>
          ) : (
            <Button disabled size="small">Activate</Button>
          )}
        </div>
      </div>
    </div>
  );

  /* ── Derive active items ─────────────────────────────────── */
  const activeConfig = TABS.find((t) => t.key === activeTab);
  const items =
    activeConfig?.key === "ads"
      ? getAdsArray(data)
      : data?.[activeConfig?.dataKey] ?? [];

  const countFor = (tab) =>
    tab.key === "ads"
      ? getAdsArray(data).length
      : data?.[tab.dataKey]?.length ?? 0;

  /* ── Render ─────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="aup-loader">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="aup-page">

      {/* ── Tab nav ─────────────────────────────────────────── */}
      <nav className="aup-nav" role="tablist" aria-label="Post categories">
        <div className="aup-nav__inner">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`aup-nav__btn${activeTab === tab.key ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="aup-nav__badge">{countFor(tab)}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* ── Content ─────────────────────────────────────────── */}
      <section className="aup-body" role="tabpanel">
        <header className="aup-body__header">
          <Title level={4}>{activeConfig.label}</Title>
        </header>

        {items.length === 0 ? (
          <div className="aup-empty">
            <Empty />
          </div>
        ) : (
          <div className="aup-grid">
            {items.map((item) => renderCard(item, activeConfig.api))}
          </div>
        )}
      </section>

      {/* ── Reject Modal ─────────────────────────────────────── */}
      <Modal
        title="Reject Listing"
        open={isModalOpen}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !reason.trim(), danger: true }}
        onOk={handleConfirmReject}
        onCancel={closeModal}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0" }}>
          <Text strong style={{ fontSize: 14, color: "#1a2b3c" }}>
            Rejection Reason
          </Text>
          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ borderRadius: 8, resize: "none" }}
          />
          {!reason.trim() && (
            <p style={{ color: "#ff4d4f", fontSize: 12, margin: 0 }}>
              Reason is required
            </p>
          )}
        </div>
      </Modal>

    </div>
  );
}

/* ── Wrap with App for message context ───────────────────── */
function AdminUserPosts() {
  return (
    <App>
      <AdminUserPostsInner />
    </App>
  );
}

export default AdminUserPosts;